<!-- app/components/workbook/ProductDisplay.vue -->
<template>
  <UContainer>
    <div ref="heroDisplayRef" class="model-sheetm mt-2 pt-5  lg:pt-18" :class="isPrint ? 'print:model-sheet' : ''"
      :id="`style-${model.Style}`">
      <UPageGrid class="mb-8 gap-5 relative">
        
        <!-- Vänster: bilder -->
        <section class="col-span-7 relative">
          <WorkbookProductInfo v-if="isMobile" :model="model" />
          <div  class="relative">  
            <div class="perspective-midrange relative pt-12 md:pt-0" :class="isPrint ? 'bg-workbook-white' : 'product-display-gradient'">
              <div @click="toggleBack" class="relative z-10 h-full w-full transition-transform duration-500 transform-3d"
                :style="{ cursor: canToggleBack ? 'pointer' : 'default' }"
                :class="{ 'rotate-y-180': showBack }">
                <section
                  class="inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden] [transform:translateZ(0)]">
                  <WorkbookProductImage :src="heroImageFront" />
                </section>
                <section v-if="!isPrint" class="absolute inset-0 z-20 rotate-y-180 backface-hidden">
                  <WorkbookProductImage :src="heroImageBack" @error="onBackImgError" />
                </section>
              </div>
            </div>
            <div class="absolute top-6 left-4 md:left-5 flex items-center gap-4">
              <div
                class="flex items-center gap-2 text-xs py-1 px-2 border border-black text-workbook-black bg-transparent">                
                <span>{{ selectedHeroColour }}</span>
              </div>
              <UButton v-if="hasBackImage && !isPrint" variant="link" size="xs"
                :label="showBack ? 'View front' : 'View back'" @click="toggleBack"
                class="font-semibold underline decoration-2 underline-offset-4 uppercase" />
            </div>

            <!-- Ikoner -->
            <div class="absolute top-8 right-5">
              <div v-if="model.name.toLowerCase().includes('guardian')">
                <WorkbookIcon class="w-17 h-17 text-workbook-black" name="workbook:guardian" />
              </div>
              <div v-else-if="model.Airmap">
                <WorkbookIcon class="w-16 h-16 text-workbook-black" name="workbook:airmap" />
              </div>
              <div v-else-if="model.WTV">
                <WorkbookIcon class="w-16 h-16 text-workbook-black" name="workbook:wtv-black" />
              </div>
              <div v-else-if="model.Helios">
                <WorkbookIcon class="w-16 h-16 text-workbook-black" name="workbook:helios" />
              </div>
            </div>

          </div>
          <!-- OTHER COLOURS -->
          <div :class="isPrint ? 'printX flex justify-start' : 'noprintX grid grid-cols-1 md:grid-cols-2'" class="gap-1 mt-1">
            <div :class="isPrint ? 'max-w-1/3' : ''" class="w-full" v-for="(variant, i) in nonHeroVariants"
              :key="variantKey(variant, i)">
              <button
                type="button"
                class="w-full relative pt-12 md:pt-0 text-left"
                :class="isPrint ? 'bg-workbook-white' : 'product-display-gradient'"
                
              >
                <WorkbookProductImage :src="imageSrc(variant)" />
                
                <div
                  class="flex items-center gap-2 absolute top-6 left-6  text-xs py-1 px-2 border border-black text-workbook-black bg-transparent">
                  
                  <span>{{ variant.Colour }}</span>
                </div>
              </button>
            </div>
          </div>
          <!-- Cart variant summary (readonly, no buttons) -->
          <div v-if="modelVariantsInCart.length" class="my-8">
            <h4 class="font-bold mb-2 text-base">Quantities for style</h4>
            <div class="w-full">
              <table class="text-sm border-collapse">
                <thead>
                  <tr>
                    <th class="text-left pr-4 py-1 font-semibold">Color</th>
                    <th class="text-left pr-4 py-1 font-semibold">Size</th>
                    <th class="text-left pr-4 py-1 font-semibold">SKU</th>
                    <th class="text-center pr-4 py-1 font-semibold">Qty</th>
                    <th class="text-right py-1 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in sortedModelVariantsInCart" :key="row.sku">
                    <td class="pr-4 py-1">{{ row.colour }}</td>
                    <td class="pr-4 py-1">{{ row.displaySize }}</td>
                    <td class="pr-4 py-1">{{ row.sku }}</td>
                    <td class="pr-4 text-center py-1 font-bold">{{ row.qty }}</td>
                    <td class="text-right py-1 font-bold">{{ formatNumber(row.totalPrice, currency) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="4" class="text-right pr-4 py-2 font-semibold">Total for style:</td>
                    <td class="text-right  py-2 font-bold">{{ formatNumber(styleTotal, currency) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <!-- Notes display for print -->
          <div v-if="isPrint && hasNote" class="mt-4">
            <h3 class="mb-2">Notes:</h3>
            <p class="text-sm leading-snug whitespace-pre-line mb-0">{{ noteText }}</p>
          </div>
          
        </section>

        <!-- Höger: info (komplett, ingen pseudo) -->
        <section class="col-span-7 lg:col-span-5 flex flex-col">
          <WorkbookProductInfo v-if="!isMobile" :model="model">
            <template #top-right>
              <div v-if="!isPrint" class="whitespace-nowrap">
                <ModelSaveToggle v-if="!model.NOTAvailableforPREOrder" :model="model" size="sm" shape="square"
                  class="text-xs"
                  save-label="SAVE TO CART" saved-label="SAVED" />
                <div v-else class="text-sm font-semibold bg-white/90 px-3 py-1 border border-red-300">
                  Not available for pre-order
                </div>
              </div>
            </template>
          </WorkbookProductInfo>
          <!-- PRODUCT DETAILS -->
          <div class="mb-4 text-sm">
            <h3 class="mb-1 text-base">Detailed description</h3>
            <p class="mb-0 whitespace-pre-line leading-snug">
              {{ model.ProductDetails || 'Not available in current datasheet.' }}
            </p>
          </div>
          <!-- SIZE & WEIGHT -->
          <div class="flex flex-wrap gap-x-8 gap-y-2 text-sm mb-0">
            <p class="mb-0" v-if="model.Weightg || model.Weight"><strong>Weight:</strong> {{ model.Weightg ||
              model.Weight }}g</p>
            <p class="mb-0" v-if="model.SizeRange"><strong>Size range:</strong> {{ model.SizeRange }}</p>
          </div>
          <div v-if="model.Features" class="mb-4 text-sm pt-4">
            <h3 class="mb-1 text-base">Product Features</h3>
            <p class="leading-snug whitespace-pre-line mb-0">
              {{ model.Features }}
            </p>
          </div>
           <div class="pt-4 mb-6 text-sm">
             <template v-if="isPrint">
               <div class="flex flex-col gap-2 w-full border-t border-black">
                 <h4 class="py-2 text-base font-semibold">{{ model.Sustainability ? 'Sustainability info' : 'Optional info 01' }}</h4>
                 <p v-if="model.Sustainability" class="text-sm pl-6 whitespace-pre-line mb-4">
                   {{ model.Sustainability }}
                 </p>
               </div>
               <div class="flex flex-col gap-2 w-full border-t border-black">
                 <h4 class="py-2 text-base font-semibold">{{ model.CompletetheKit ? 'Complete the kit' : 'Optional info 02' }}</h4>
                 <p v-if="model.CompletetheKit" class="text-sm pl-6 whitespace-pre-line mb-4">
                   {{ model.CompletetheKit }}
                 </p>
               </div>
               <div class="flex flex-col gap-2 w-full border-y border-black">
                 <h4 class="py-2 text-base font-semibold">Optional info</h4>
                 <p  class="text-sm pl-6 whitespace-pre-line mb-4">
                   Extra info can go here.
                 </p>
               </div>
             </template>
             <template v-else>
               <UCollapsible class="flex flex-col gap-2 w-full">
                 <UButton class="py-2 group text-base font-semibold  border-t border-black"
                 variant="link"
                   trailing-icon="i-lucide-plus" :ui="{
                     trailingIcon: 'group-data-[state=open]:rotate-45 transition-transform duration-200 scale-60'
                   }" block>{{ model.Sustainability ? 'Sustainability info' : 'Optional info 01' }}</UButton>
                 <template #content>
                   <p v-if="model.Sustainability" class="text-sm pl-6 whitespace-pre-line mb-4">
                     {{ model.Sustainability }}
                   </p>
                 </template>
               </UCollapsible>
               <UCollapsible class="flex flex-col gap-2 w-full">
                 <UButton class="py-2 group text-base font-semibold  border-t border-black"
                   variant="link"
                   trailing-icon="i-lucide-plus" :ui="{
                     trailingIcon: 'group-data-[state=open]:rotate-45 transition-transform duration-200 scale-60'
                   }" block>{{ model.CompletetheKit ? 'Complete the kit' : 'Optional info 02' }}</UButton>
                 <template #content>
                   <p v-if="model.CompletetheKit" class="text-sm pl-6 whitespace-pre-line mb-4">
                     {{ model.CompletetheKit }}
                   </p>
                 </template>
               </UCollapsible>
               <UCollapsible class="flex flex-col gap-2 w-full border-b border-black">
                 <UButton class="py-2 group text-base font-semibold  border-t border-black"
                   variant="link"
                   trailing-icon="i-lucide-plus" :ui="{
                     trailingIcon: 'group-data-[state=open]:rotate-45 transition-transform duration-200 scale-60'
                   }" block>Optional info</UButton>
                 <template #content>
                   <p  class="text-sm pl-6 whitespace-pre-line mb-4">
                     Extra info can go here.
                   </p>
                 </template>
               </UCollapsible>
             </template>
           </div>
         
          <!-- Notes section (interactive) -->
          <div class="mt-0 no-print" v-if="!isPrint">
            <UCollapsible class="flex flex-col gap-2 w-full">
              
              <UButton class="group p-4 bg-workbook-yellow text-workbook-black text-base border border-[#C5C089]"
              variant="link"
                trailing-icon="i-lucide-plus" :ui="{
                  trailingIcon: 'group-data-[state=open]:rotate-45 transition-transform duration-200 scale-60'
                }" block>{{ hasNote ? 'Edit notes' : 'My notes for this product' }}</UButton>

              <template #content>
                <UTextarea 
                  v-model="noteText" 
                  :rows="5" 
                  placeholder="Add your notes about this product here..."
                  class="w-full"
                  @update:model-value="onNoteChange"
                />
              </template>
            </UCollapsible>

            <UDropdownMenu class="mt-4" :items="exportMenuItems">
              <UButton class="cursor-pointer" :label="isExporting ? 'Exporting…' : 'PDF Export'" color="primary"
                variant="link" trailing-icon="workbook:pdf" :disabled="isExporting" :loading="isExporting" />
            </UDropdownMenu>

          </div>
          
        </section>
      </UPageGrid>
      <div class="pt-6" v-if="model.DetailDescription1 || model.DetailDescription2 || model.DetailDescription3">
        <h3 class="text-2xl font-normal mb-4">Product details</h3>
        <UPageGrid class="gap-6">
          <div class="col-span-4 text-base text-center" v-if="model.DetailDescription1">
            <WorkbookDetailImage :src="detailImage(model, 1)" alt="Detail Image 1" class="mb-4" />
            {{ model.DetailDescription1 }}
          </div>
          <div class="col-span-4 text-base text-center" v-if="model.DetailDescription2">
            <WorkbookDetailImage :src="detailImage(model, 2)" alt="Detail Image 2" class="mb-4" />
            {{ model.DetailDescription2 }}
          </div>
          <div class="col-span-4 text-base  text-center" v-if="model.DetailDescription3">
            <WorkbookDetailImage :src="detailImage(model, 3)" alt="Detail Image 3" class="mb-4" />
            {{ model.DetailDescription3 }}
          </div>
        </UPageGrid>
      </div>
    </div>
  </UContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import type { CatalogItem, ProductModel } from '@/types/catalog'
import { useCartStore } from '@/store/cart'
import { useSettingsStore } from '@/store/settings'
import { useNotesStore } from '@/store/notes'
import { useAuthStore } from '@/store/auth'
import { usePdfExport } from '@/composables/usePdfExport'
import { formatNumber } from '@/utils/formatNumber'

import ModelSaveToggle from '@/components/cart/ModelSaveToggle.vue'
import { imageForColour, imageBackForColour, detailImage } from '@/utils/productImages'
import WorkbookGague from '@/components/workbook/Gague.vue'
import WorkbookProductImage from '@/components/workbook/ProductImage.vue'
import WorkbookDetailImage from '@/components/workbook/DetailImage.vue'

import { useWindowSize } from '@vueuse/core'
const { width } = useWindowSize()

const CANONICAL_ORDER = ['One Size', 'XS', 'SM', 'MD', 'LG', 'XL', 'XXL', 'XXXL'] as const

function normalizeSizeCode(raw: string): string {
  const s = String(raw || '').trim().toUpperCase()
  if (!s) return ''
  if (['ONE SIZE', 'OS', 'O/S'].includes(s)) return 'One Size'
  if (s === 'XS') return 'XS'
  if (s === 'S' || s === 'SM') return 'SM'
  if (s === 'M' || s === 'MD') return 'MD'
  if (s === 'L' || s === 'LG') return 'LG'
  if (s === 'XL') return 'XL'
  if (s === 'XXL' || s === '2XL') return 'XXL'
  if (s === 'XXXL' || s === '3XL') return 'XXXL'
  return raw
}
function sizeSortIndex(size: string): number {
  const norm = normalizeSizeCode(size)
  const idx = CANONICAL_ORDER.indexOf(norm as any)
  return idx === -1 ? 1000 + norm.charCodeAt(0) : idx
}

const isMobile = computed(() => width.value < 767)

const props = withDefaults(defineProps<{ model: ProductModel; isPrint?: boolean }>(), {
  isPrint: false
})

function uniqueColours(items: CatalogItem[]): CatalogItem[] {
  const seen = new Set<string>()
  return items.filter(it => {
    const key = `${it.Style}-${it.Colour}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const primaryVariant = props.model.variants[0]
const uniqueVariants = uniqueColours(props.model.variants)
const selectedHeroColour = ref(primaryVariant.Colour)
const heroImageFront = computed(() => imageForColour(props.model, selectedHeroColour.value))
const heroImageBack = computed(() => imageBackForColour(props.model, selectedHeroColour.value))

const showBack = ref(false)
const hasBackImage = ref(true);
const heroDisplayRef = ref<HTMLElement | null>(null)

watch(selectedHeroColour, async (newColour, oldColour) => {
  showBack.value = false
  hasBackImage.value = true

  if (newColour === oldColour) return

  await nextTick()
  heroDisplayRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
})

function onBackImgError() {
  hasBackImage.value = false;
}

function toggleBack() {
  if (!canToggleBack.value) return
  showBack.value = !showBack.value
}

const nonHeroVariants = computed(() => {
  const heroIndex = uniqueVariants.findIndex(v => v.Colour === selectedHeroColour.value)
  if (heroIndex < 0) return uniqueVariants.slice(1)

  const rotatedVariants = [
    ...uniqueVariants.slice(heroIndex),
    ...uniqueVariants.slice(0, heroIndex)
  ]
  return rotatedVariants.slice(1)
})

function setHeroColour(colour: string) {
  selectedHeroColour.value = colour
}

function imageSrc(variant: CatalogItem) {
  return imageForColour(props.model, variant.Colour)
}
function variantKey(v: CatalogItem, i: number) {
  return v.SKU || `${v.Style}-${v.Colour}-${i}`
}

const cart = useCartStore()
cart.hydrate()
const settings = useSettingsStore()
settings.hydrate()
const currency = computed(() => settings.currency)
const isPrint = computed(() => props.isPrint === true)
const canToggleBack = computed(() => hasBackImage.value && !isPrint.value)

function wsFieldForCurrency(cur: string): string {
  switch (cur) {
    case 'USD': return 'WSUSD'
    case 'EUR': return 'WSEUR'
    case 'GBP': return 'WSGBP'
    case 'CHF': return 'WSCHF'
    case 'CAD':
    default:
      return 'WSCAD'
  }
}

const modelVariantsInCart = computed(() => {
  if (!cart.hydrated) return []
  const field = wsFieldForCurrency(currency.value)
  return props.model.variants
    .filter(v => v.SKU && cart.lineItems[v.SKU] && cart.lineItems[v.SKU].qty > 0)
    .map(v => {
      const li = cart.lineItems[v.SKU]
      const price = Number(v[field] ?? props.model[field])
      return {
        sku: v.SKU,
        colour: v.Colour,
        size: v.Size,
        displaySize: normalizeSizeCode(v.SizeCode || v.Size || ''),
        qty: li.qty,
        unitPrice: price,
        totalPrice: Number.isFinite(price) ? price * li.qty : 0
      }
    })
})

const sortedModelVariantsInCart = computed(() => {
  return [...modelVariantsInCart.value].sort((a, b) => {
    if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
    return sizeSortIndex(a.displaySize) - sizeSortIndex(b.displaySize)
  })
})

const styleTotal = computed(() => {
  return sortedModelVariantsInCart.value.reduce((sum, row) => sum + row.totalPrice, 0)
})

const { isExporting, exportLineOverview, exportWorkbook } = usePdfExport()
const savedStyles = computed(() => Array.from(cart.savedStyles))
const selectedProductStyles = computed(() => savedStyles.value)
const exportMenuItems = computed(() => {
  const hasSelectedProducts = savedStyles.value.length > 0
  const workbookMode = settings.workbookMode || 'All'
  const items = [
    [{ type: 'label', label: 'Line Overview to PDF:' }],
    [
      {
        label: 'Complete Line Overview: ' + workbookMode,
        disabled: isExporting.value,
        onSelect: () =>
          exportLineOverview({
            size: 'A1',
            orientation: 'portrait',
            filters: {},
            mode: workbookMode
          })
      },
      {
        label: 'Selected products',
        disabled: isExporting.value || !hasSelectedProducts,
        onSelect: () =>
          exportLineOverview({
            size: 'A1',
            orientation: 'portrait',
            filters: { styles: selectedProductStyles.value },
            mode: workbookMode
          })
      }
    ]
  ]

  items.push([{ type: 'label', label: 'Workbook to PDF:' }])
  items.push([
    {
      label: 'Complete workbook: ' + workbookMode,
      disabled: isExporting.value,
      onSelect: () => exportWorkbook({ all: true, useFilters: false, filters: undefined, categories: undefined, mode: workbookMode })
    },
    {
      label: 'Selected products only',
      disabled: isExporting.value || !hasSelectedProducts,
      onSelect: () => exportWorkbook({ styles: selectedProductStyles.value, mode: workbookMode })
    },
    {
      label: 'Current Cart',
      disabled: isExporting.value || !hasSelectedProducts,
      onSelect: () => exportWorkbook({ styles: selectedProductStyles.value, cart: true, mode: workbookMode })
    },
    {
      label: 'Only this product',
      disabled: isExporting.value,
      onSelect: () => exportWorkbook({ styles: [props.model.Style], mode: workbookMode, singleProduct: true })
    }
  ])

  return items
})

// Notes functionality
const notesStore = useNotesStore()
const auth = useAuthStore()
notesStore.hydrate()

const noteText = ref('')
const hasNote = computed(() => notesStore.hasNoteForStyle(props.model.Style))

// Load note on mount asynchronously
onMounted(async () => {
  if (auth.user?.uid || auth.user?.email) {
    // Async load note for this specific style
    await notesStore.hydrateStyleNoteFromFirestore(
      auth.user.uid || auth.user.email,
      props.model.Style
    )
  }
  noteText.value = notesStore.getNoteForStyle(props.model.Style)
})

// Debounced save function
let saveTimeout: NodeJS.Timeout | null = null
function onNoteChange(value: string) {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(() => {
    if (value.trim()) {
      notesStore.setNote(props.model.Style, value)
    } else {
      notesStore.deleteNote(props.model.Style)
    }
  }, 500)
}
</script>

<style scoped>
@reference "../../assets/css/main.css";


</style>
